import { useState, useEffect } from "react";

import withStyles from "@material-ui/core/styles/withStyles";
import Check from "@material-ui/icons/Check";
import Button from "~/material-dashboard/components/CustomButtons/Button.jsx";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import GridContainer from "~/material-dashboard/components/Grid/GridContainerWithSpacing.jsx";
import GridItem from "~/material-dashboard/components/Grid/GridItemWithSpacing.jsx";
import {
  getUserID,
  getVisitEmail,
  getPostCheckoutInfo,
  getVisitPhone,
  getVisitDifferentPharmacyJSON,
  setOrderComplete,
} from "~/visits/utils/visitCookies.js";
import PersonaVerificationDialog from "~/shared/widgets/PersonaVerificationDialog.jsx";

import { ProductDescription } from "./paymentComponents";

import consultationStyles from "~/visits/styles/consultationStyles.js";

import { kickBlue } from "~/shared/styles/baseStyles.js";
import { useQueryParams } from "~/shared/utils/sharedUtils.js";

const buttonStyles = {
  fontWeight: "bold",
  backgroundColor: kickBlue,
  textTransform: "none",
  fontSize: "14px",
  marginBottom: "20px",
};
const outlinedButtonStyles = {
  ...buttonStyles,
  border: "1px solid",
  backgroundColor: "white",
  color: kickBlue,
};

const PaymentComplete = (props) => {
  const { classes } = props;
  const { consultationType } = props.match.params;
  const queryParams = useQueryParams();
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [showPersonaDialog, setShowPersonaDialog] = useState(
    queryParams.get("requiresIDVerification")
  );

  const [hasFetchedOpenInquiryID, setHasFetchedOpenInquiryID] = useState(false);
  const [openInquiryID, setOpenInquiryID] = useState("");

  useEffect(() => {
    setOrderComplete(checkoutSessionID);
  }, []);

  useEffect(() => {
    if (showPersonaDialog && !hasFetchedOpenInquiryID) {
      fetch(`/api/v1/open-persona-inquiry`)
        .then(async (data) => {
          const body = await data.json();
          if (body.isPersonaVerified) {
            setShowPersonaDialog(false);
            props.history.push(window.location.pathname);
            return;
          } else {
            setOpenInquiryID(body.openInquiryID);
          }
        })
        .finally(() => {
          setHasFetchedOpenInquiryID(true);
        });
    }
  }, []);

  function PersonaDialog() {
    if (!showPersonaDialog || !hasFetchedOpenInquiryID) return null;

    const postCheckoutInfo = getPostCheckoutInfo(checkoutSessionID);

    // mobile device - use hosted flow because facebook browser seems to have issues with camera permissions causing page to disappear
    if (isMobile) {
      let personaURL = `https://kick.withpersona.com/verify?reference-id=${getUserID()}`;
      if (openInquiryID) {
        personaURL += `&inquiry-id=${openInquiryID}`;
      } else {
        personaURL += `&template-id=itmpl_q7qB2rQNwtmo13CzwUX25sqS`;
      }
      const cleanURL = new URL(window.location.href);
      cleanURL.searchParams.delete("requiresIDVerification");
      personaURL += `&fields[visit-id]=${
        postCheckoutInfo.visitID
      }&fields[email-address]=${getVisitEmail()}&fields[phone-number]=${getVisitPhone()}&redirect-uri=${encodeURIComponent(
        cleanURL.toString()
      )}`;

      window.location.href = personaURL;
      return null;
    }

    return (
      <PersonaVerificationDialog
        fullScreen={isMobile}
        userID={getUserID()}
        email={getVisitEmail()}
        phoneNumber={getVisitPhone()}
        visitID={postCheckoutInfo.visitID}
        openInquiryID={openInquiryID}
        onComplete={({ inquiryId, status, fields }) => {
          setShowPersonaDialog(false);
        }}
      />
    );
  }

  const localPharmacyPickup = getVisitDifferentPharmacyJSON(checkoutSessionID);

  return (
    <div
      className={`${classes.containerWithSpacing} payment-section`}
      style={{ minHeight: "60vh", justifyContent: "center" }}
    >
      <PersonaDialog />
      <GridContainer>
        <GridItem
          xs={12}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div className={classes.paymentCompleteCongrats}>
            <div
              style={{
                borderRadius: "100%",
                backgroundColor: kickBlue,
                height: "60px",
                width: "60px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <Check style={{ color: "#fff", fontSize: "42px" }} />
            </div>
            <p
              className={classes.subtitle}
              style={{ textAlign: "center", marginBottom: "16px" }}
            >
              Congratulations!
            </p>
            <p className={classes.subtitle} style={{ textAlign: "center" }}>
              Your order is confirmed!
            </p>
          </div>
          <ProductDescription
            classes={classes}
            consultationType={consultationType}
            center={true}
            checkoutSessionID={checkoutSessionID}
            postCheckout={true}
          />

          {localPharmacyPickup && (
            <div
              style={{
                textAlign: "center",
                width: "100%",
                borderBottom: "1px solid #ddd",
                paddingBottom: "28px",
                marginBottom: "20px",
                maxWidth: "800px",
              }}
            >
              <h5 style={{ fontWeight: "bold", fontSize: "16px" }}>
                Your visit is in line for a doctor to review. Once reviewed,
                Your prescription will be sent to:
              </h5>
              <div>{localPharmacyPickup.pharmacyName}</div>
              <div>
                {localPharmacyPickup.pharmacyAddress1}{" "}
                {localPharmacyPickup.pharmacyAddress2}
              </div>
              <div>
                {localPharmacyPickup.pharmacyCity},{" "}
                {localPharmacyPickup.pharmacyState}{" "}
                {localPharmacyPickup.pharmacyZip}
              </div>
            </div>
          )}
          <p style={{ fontWeight: "bold", fontSize: "16px" }}>
            We've sent the details of your order to your registered email:{" "}
            {getVisitEmail()}
          </p>
          <br />
          <p style={{ fontWeight: "bold", fontSize: "16px" }}>
            Please check your inbox in a few minutes to ensure you've received
            our confirmation.
          </p>
          <Button
            href="https://gokick.com/treatment"
            style={{
              ...outlinedButtonStyles,
              width: isMobile ? "100%" : "320px",
              marginTop: "40px",
            }}
          >
            Treatment Plans
          </Button>
          <Button
            href="https://gokick.com/guides/anxiety/propranolol"
            style={{
              ...outlinedButtonStyles,
              width: isMobile ? "100%" : "320px",
            }}
          >
            Learn More About Beta Blockers
          </Button>
          <Button
            href="https://gokick.com/sleep"
            style={{
              ...outlinedButtonStyles,
              width: isMobile ? "100%" : "320px",
            }}
          >
            Learn More About Sleep
          </Button>
          <Button
            href="https://gokick.com/blog"
            style={{
              ...outlinedButtonStyles,
              width: isMobile ? "100%" : "320px",
            }}
          >
            Check Out Our Blog
          </Button>
          <Button
            href="/me/portal/"
            style={{
              ...buttonStyles,
              width: isMobile ? "100%" : "320px",
            }}
          >
            Back to My Account
            <NavigateNextIcon />
          </Button>
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default withStyles(consultationStyles)(PaymentComplete);
